import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { Trophy, Medal, Award, User, Filter, RefreshCw } from "lucide-react";

/**
 * PointsLeaderboard - Ranked employee list with period/role filters
 * 
 * Usage:
 * <PointsLeaderboard />
 * 
 * Features:
 * - Period filter: Today, Week, Month
 * - Role filter: All, Technician, Helper, Supervisor, Surveyor
 * - Shows rank, name, points, tasks, and average daily points
 */
const PointsLeaderboard = ({ className = "", defaultPeriod = "month" }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [period, setPeriod] = useState(defaultPeriod);
  const [role, setRole] = useState("");
  
  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, limit: 20 });
      if (role) params.append("role", role);
      
      const res = await api.get(`/gamification/points/leaderboard?${params}`);
      setLeaderboard(res.data.leaderboard || []);
      setError(null);
    } catch (e) {
      console.error("Failed to fetch leaderboard:", e);
      setError("Unable to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [period, role]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={20} className="text-yellow-500" />;
    if (rank === 2) return <Medal size={20} className="text-slate-400" />;
    if (rank === 3) return <Award size={20} className="text-amber-600" />;
    return <span className="w-5 text-center text-slate-500 font-bold">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200";
    if (rank === 2) return "bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200";
    if (rank === 3) return "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200";
    return "bg-white border-slate-100";
  };

  return (
    <div className={`bg-white rounded-lg border border-slate-200 shadow-sm ${className}`} data-testid="points-leaderboard">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-orange-500" />
            <h3 className="font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>
              Points Leaderboard
            </h3>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Period Filter */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              {["today", "week", "month"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                    period === p
                      ? "bg-orange-500 text-white shadow-sm"
                      : "text-slate-600 hover:text-orange-600"
                  }`}
                  data-testid={`period-filter-${p}`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
            
            {/* Role Filter */}
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="role-filter"
            >
              <option value="">All Roles</option>
              <option value="technician">Technician</option>
              <option value="helper">Helper</option>
              <option value="supervisor">Supervisor</option>
              <option value="surveyor">Surveyor</option>
            </select>
            
            {/* Refresh */}
            <button
              onClick={fetchLeaderboard}
              className="p-1.5 text-slate-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
              data-testid="refresh-leaderboard"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
        {loading && leaderboard.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <RefreshCw size={24} className="mx-auto mb-2 animate-spin text-orange-500" />
            <p className="text-sm">Loading leaderboard...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            <p className="text-sm">{error}</p>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Trophy size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No points data available for this period</p>
          </div>
        ) : (
          leaderboard.map((entry) => (
            <div
              key={entry.employee_id}
              className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-l-4 ${getRankBg(entry.rank)}`}
              data-testid={`leaderboard-entry-${entry.rank}`}
            >
              {/* Left: Rank & Name */}
              <div className="flex items-center gap-4">
                <div className="w-8 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <div>
                  <p className="font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>
                    {entry.employee_name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full capitalize">
                      {entry.role}
                    </span>
                    {entry.circle_name && (
                      <span className="text-xs text-slate-400">
                        {entry.circle_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Points & Stats */}
              <div className="text-right">
                <p className="text-xl font-black text-orange-600" style={{ fontFamily: "Manrope" }}>
                  {entry.total_points.toFixed(1)}
                </p>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                  <span>{entry.total_tasks} tasks</span>
                  <span className="text-slate-300">|</span>
                  <span>~{entry.avg_daily_points.toFixed(1)}/day</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {leaderboard.length > 0 && (
        <div className="p-3 bg-slate-50 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            Showing top {leaderboard.length} performers
          </p>
        </div>
      )}
    </div>
  );
};

export default PointsLeaderboard;
